import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { useTranslation } from "@/hooks/useTranslation";
import {
  TeamInvitationDTO,
  TeamLicenseInventoryDTO,
  TeamUserDTO,
  TeamUserRole,
} from "@shaarp/common";
import { ColumnDef } from "@tanstack/react-table";
import { MailIcon } from "lucide-react";
import DataTableCellUserInvitationActions from "../data-table-users-invitations/data-table-cell-user-invitation-actions";
import DataTableCellUserInvitationLicence from "../data-table-users-invitations/data-table-cell-user-invitation-licence";
import DataTableCellUserInvitationRole from "../data-table-users-invitations/data-table-cell-user-invitation-role";
import DataTableCellUserActions from "../data-table-users/data-table-cell-user-actions";
import DataTableCellUserInstruction from "../data-table-users/data-table-cell-user-instruction";
import DataTableCellUserLicence from "../data-table-users/data-table-cell-user-licence";
import DataTableCellUserPinnedPrompts from "../data-table-users/data-table-cell-user-pinned-prompts";
import DataTableCellUserRole from "../data-table-users/data-table-cell-user-role";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import DataTableCellMemberStatus from "./data-table-cell-member-status";

export type TeamMemberRow = {
  kind: "user" | "invitation";
  id: string;
  email: string;
  fullName: string;
  role: TeamUserRole;
  priceId: string | null;
  user?: TeamUserDTO;
  invitation?: TeamInvitationDTO;
};

export const dataTableTeamMembersColumns = (
  inventory?: TeamLicenseInventoryDTO,
  onInventoryRefetch?: () => Promise<void>
) => {
  const { t } = useTranslation();

  return [
    {
      accessorKey: "avatar",
      header: "",
      cell: ({ row }) => {
        const member = row.original;
        if (member.kind === "user" && member.user) {
          return (
            <div className="flex justify-center">
              <Avatar className="w-12 h-12">
                <AvatarImage
                  src={member.user.imageUrl}
                  alt={member.user.fullName}
                />
              </Avatar>
            </div>
          );
        }
        return (
          <div className="flex justify-center">
            <Avatar className="w-12 h-12">
              <AvatarFallback>
                <MailIcon className="w-5 h-5 text-muted-foreground" />
              </AvatarFallback>
            </Avatar>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: "Nom",
      accessorKey: "fullName",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableTeamMembers.columns.name")}
        />
      ),
      cell: ({ row }) => {
        const member = row.original;
        if (member.kind === "user") {
          return <span className="font-bold">{member.fullName}</span>;
        }
        return (
          <span className="italic text-muted-foreground">
            {t("components.dataTableTeamMembers.invitedUserPlaceholder")}
          </span>
        );
      },
    },
    {
      id: "Email",
      accessorKey: "email",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableTeamMembers.columns.email")}
        />
      ),
    },
    {
      id: "Rôle",
      accessorKey: "role",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableTeamMembers.columns.role")}
        />
      ),
      cell: ({ row }) => {
        const member = row.original;
        if (member.kind === "user" && member.user) {
          return <DataTableCellUserRole user={member.user} />;
        }
        if (member.invitation) {
          return (
            <DataTableCellUserInvitationRole invitation={member.invitation} />
          );
        }
        return null;
      },
    },
    {
      id: "Licence",
      accessorKey: "priceId",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableTeamMembers.columns.licence")}
        />
      ),
      cell: ({ row }) => {
        const member = row.original;
        if (member.kind === "user" && member.user) {
          return (
            <DataTableCellUserLicence
              user={member.user}
              inventory={inventory}
              onInventoryRefetch={onInventoryRefetch}
            />
          );
        }
        if (member.invitation) {
          return (
            <DataTableCellUserInvitationLicence
              invitation={member.invitation}
            />
          );
        }
        return null;
      },
    },
    {
      id: "Statut",
      accessorKey: "kind",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableTeamMembers.columns.status")}
        />
      ),
      cell: ({ row }) => {
        const member = row.original;
        return <DataTableCellMemberStatus member={member} />;
      },
      sortingFn: (rowA, rowB) => {
        const orderOf = (m: TeamMemberRow): number => {
          if (m.kind === "invitation") return 3;
          if (!m.priceId) return 2;
          if (m.user?.scheduledUnassignAt) return 1;
          return 0;
        };
        return orderOf(rowA.original) - orderOf(rowB.original);
      },
    },
    {
      id: "Instruction IA",
      accessorKey: "instruction",
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableTeamMembers.columns.aiInstruction")}
        />
      ),
      cell: ({ row }) => {
        const member = row.original;
        if (member.kind === "user" && member.user) {
          return <DataTableCellUserInstruction user={member.user} />;
        }
        return null;
      },
    },
    {
      id: "Tactiques épinglées",
      accessorKey: "pinnedPrompts",
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("components.dataTableTeamMembers.columns.pinnedPrompts")}
        />
      ),
      cell: ({ row }) => {
        const member = row.original;
        if (member.kind === "user" && member.user) {
          return <DataTableCellUserPinnedPrompts user={member.user} />;
        }
        return null;
      },
    },
    {
      id: "actions",
      cell: ({ row }) => {
        const member = row.original;
        return (
          <div className="flex justify-end">
            {member.kind === "user" && member.user && (
              <DataTableCellUserActions user={member.user} />
            )}
            {member.kind === "invitation" && member.invitation && (
              <DataTableCellUserInvitationActions
                invitation={member.invitation}
              />
            )}
          </div>
        );
      },
    },
  ] as ColumnDef<TeamMemberRow>[];
};
